import { PublicCallback } from 'globals/types';
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { getSessionFromStore, getStore } from 'core_main/store';
import { getSession } from 'core_main/api/sessions';
import * as Pubnub from 'pubnub';
import { fetchMessageAttachments } from 'core_main/api/messages';
import { DEFAULT_PUBNUB_MESSAGE_PAGE_SIZE } from 'core/constants';
import { handleError } from 'globals/errors';
import { subscribeToTypingIndicatorChannel } from 'core_main/helpers/typingIndicator';
import { createGetMessagesCallbackResponse } from './helpers';
import {
  GetAllMessagesResolve,
  PNMessagesListMessage,
  GetMessagesParameters,
} from './types';

const getOrFetchSession = (conversationId) => {
  const session = getSessionFromStore(conversationId);
  return session
    ? Promise.resolve(session)
    : getSession(conversationId).then(() =>
        getSessionFromStore(conversationId),
      );
};

// Re-export for backward compatibility
export { subscribeToTypingIndicatorChannel };

const addAttachmentsToPNMessages = (history) => {
  const messagePromises = history?.messages.map((val) => {
    return new Promise((resolve) => {
      const attachments = val?.message?.data?.relationships?.attachments;

      if (attachments) {
        fetchMessageAttachments(val.message.data.id).then(
          (attachmentsResponses) => {
            resolve({ ...val, attachments: attachmentsResponses });
          },
        );
        return;
      }

      resolve(val);
    });
  }) as Promise<PNMessagesListMessage>[];

  return Promise.allSettled(messagePromises).then((res) => {
    const messageList: PNMessagesListMessage[] = [];
    res.forEach((message) => {
      if (message.status !== 'rejected') {
        messageList.push(message.value);
      } else {
        handleError(message.reason, {
          message: 'Error appending attachment(s) to PubNub message',
        });
      }
    });
    return {
      messages: messageList,
    };
  });
};

export const getAllPNMessages = (
  channelName: string,
  pubnub: Pubnub,
): Promise<GetAllMessagesResolve> => {
  return new Promise((resolve, reject) => {
    if (!pubnub) {
      reject(new Error('PubNub instance not found'));
    }
    const fetchMessages = (
      timetoken: Pubnub.FetchMessagesParameters['start'],
      previousMessages: PNMessagesListMessage[] = [],
    ) => {
      return pubnub
        .fetchMessages({
          channels: [channelName],
          start: timetoken,
          count: DEFAULT_PUBNUB_MESSAGE_PAGE_SIZE,
        })
        .then((response: Pubnub.FetchMessagesResponse) => {
          const messages = response?.channels?.[channelName] || [];
          const allMessages = [...previousMessages, ...messages];
          if (
            !messages?.length ||
            messages.length < DEFAULT_PUBNUB_MESSAGE_PAGE_SIZE
          ) {
            return resolve({
              messages: allMessages,
            });
          }
          const start = messages[0].timetoken;
          return fetchMessages(start, allMessages);
        })
        .catch((e) => {
          handleError(e, {
            message: 'Failed to get messages from pubnub',
          });
          reject(e);
        });
    };

    fetchMessages(0);
  });
};

export const getMessages = (
  parameters: GetMessagesParameters,
  cb?: PublicCallback,
) => {
  let pubnub;

  const conversationId = parameters?.conversationId;

  return importAndInitializePubNub()
    .then((module) => {
      pubnub = module;
      return getOrFetchSession(conversationId);
    })
    .then((conversation) => {
      const pubNubChannels = conversation?.attributes?.pubnub?.data?.channels;
      const store = getStore();
      const settings = store?.settings || {};
      const { showTypingIndicatorCustomerWeb, showTypingIndicatorWeb } =
        settings;

      const shouldSubscribeToSharedChannels =
        showTypingIndicatorCustomerWeb || showTypingIndicatorWeb;

      if (!pubNubChannels)
        throw new Error('Conversation PubNub channels not found');

      const conversationChannel = pubNubChannels.find(
        (channel) => channel.type === 'conversation',
      );

      if (!store?.isPubNubConnected) {
        return Promise.reject(new Error("PubNub isn't connected"));
      }
      if (!conversationChannel) {
        return Promise.reject(
          new Error(
            `No conversation channel found for conversation: ${conversationId}`,
          ),
        );
      }

      const sharedConvoChannel = pubNubChannels.find(
        (c) => c.type === 'conversation',
      );
      if (shouldSubscribeToSharedChannels && sharedConvoChannel) {
        const sharedConversationChannelString = sharedConvoChannel.name.replace(
          'ext-convo-',
          'shrd-convo-',
        );

        if (sharedConversationChannelString.length) {
          pubnub.getMetaPubNub()?.instance?.subscribe({
            channels: [sharedConversationChannelString],
          });
        }
      }

      return getAllPNMessages(
        conversationChannel.name,
        pubnub.getConversationPubNub()?.instance,
      );
    })

    .then(addAttachmentsToPNMessages)
    .then((history) => {
      const callbackResponse = createGetMessagesCallbackResponse(
        history?.messages,
        conversationId,
      );

      if (typeof cb === 'function') {
        cb(callbackResponse);
      }
    })
    .catch((error) => {
      handleError(error, { message: 'Failed to get messages' });
      if (typeof cb === 'function') cb(null, error);
    });
};
